<script setup lang="ts">
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import VTag from "@verbleif/lib/src/components/Tag/VTag.vue";
import { useDialogStack } from "@verbleif/lib/src/composables/Dialog/useDialogStack";
import Avatar from "@verbleif/shared/src/components/Avatar/Avatar.vue";
import { useAccountsStore } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { useNotificationsV2 } from "@verbleif/shared/src/composables/Notifications/useNotificationsV2";
import InboxModal from "@verbleif/shared/src/composables/Notifications/v2/InboxModal.vue";
import { QUIET_HOURS_CHANNEL_QUERY_PARAM, QUIET_HOURS_WINDOW_ENDS_AT_QUERY_PARAM } from "@verbleif/shared/src/composables/Notifications/v2/inboxNotificationLink";
import QuietHoursSummaryModal from "@verbleif/shared/src/composables/Notifications/v2/QuietHoursSummaryModal.vue";
import { accountContextLine, MAX_VISIBLE_OTHER_ACCOUNTS } from "@web/features/Auth/Accounts/accountContext";
import { useAccountDialogs } from "@web/features/Auth/Accounts/useAccountDialogs";
import { useLogout } from "@web/features/Auth/Logout/useLogout";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import NavbarLocaleChange from "./NavbarLocaleChange/NavbarLocaleChange.vue";
import NavbarNotifications from "./NavbarNotifications/NavbarNotifications.vue";
import { useProvideNavbarNotifications } from "./NavbarNotifications/useNavbarNotifications";
import NavbarSettingsItem from "./NavbarSettingsItem.vue";

const authStore = useAuthStore();
const accountsStore = useAccountsStore();
const route = useRoute();
const router = useRouter();
const locationStore = useLocationStore();
const { switchAccount, addAccount, reloginAccount } = useLogout();
const { openAccountsDialog, requestLogout } = useAccountDialogs();
const { t } = useI18n();
const TRANSLATION_KEY = "navbar.dropdown";
const dialogStack = useDialogStack();
const { isV2Enabled } = useNotificationsV2();

function clearQuietHoursQuery() {
  if (route.query[QUIET_HOURS_CHANNEL_QUERY_PARAM] || route.query[QUIET_HOURS_WINDOW_ENDS_AT_QUERY_PARAM]) {
    const {
      [QUIET_HOURS_CHANNEL_QUERY_PARAM]: _channel,
      [QUIET_HOURS_WINDOW_ENDS_AT_QUERY_PARAM]: _windowEndsAt,
      ...rest
    } = route.query;
    void router.replace({ query: rest });
  }
}

function openInbox() {
  dialogStack.push({
    id: "inbox-modal",
    component: InboxModal,
    size: "large",
    headless: true,
  });
}

// The quiet-hours summary lists inbox items, so it only opens with the new notification module on.
watch(
  [() => route.query, isV2Enabled],
  ([query, enabled]) => {
    if (!enabled) {
      return;
    }
    const channelRaw = query[QUIET_HOURS_CHANNEL_QUERY_PARAM];
    const windowEndsAtRaw = query[QUIET_HOURS_WINDOW_ENDS_AT_QUERY_PARAM];
    if (typeof channelRaw !== "string" || typeof windowEndsAtRaw !== "string") {
      return;
    }
    const channel = Number.parseInt(channelRaw, 10);
    if (Number.isNaN(channel)) {
      return;
    }
    dialogStack.push({
      id: "quiet-hours-summary",
      component: QuietHoursSummaryModal,
      size: "large",
      headless: true,
      props: {
        channel,
        windowEndsAt: windowEndsAtRaw,
      },
      onClose: clearQuietHoursQuery,
    });
  },
  { immediate: true },
);

const {
  unreadCount,
} = useProvideNavbarNotifications();

const notificationCount = computed(() => unreadCount.value);

const accountCount = computed(() => accountsStore.accounts.value.length);
const hasMultipleAccounts = computed(() => accountCount.value > 1);
const visibleOtherAccounts = computed(() => accountsStore.otherAccounts.value.slice(0, MAX_VISIBLE_OTHER_ACCOUNTS));
const hasHiddenAccounts = computed(() => accountsStore.otherAccounts.value.length > MAX_VISIBLE_OTHER_ACCOUNTS);

const locationName = computed(() => locationStore.selectedLocationObject.value?.name ?? null);
const clientName = computed(() => locationStore.selectedClient.value?.name ?? null);
const activeContext = computed(() => accountContextLine({
  locationName: locationName.value,
  clientName: clientName.value,
}));

// Remembered per account, so the list of other accounts can show where each one was working.
watch([locationName, clientName], ([location, client]) => {
  if (location === null) {
    return;
  }
  accountsStore.updateActiveContext({ locationName: location, clientName: client });
}, { immediate: true });

const triggerLabel = computed(() => hasMultipleAccounts.value
  ? t(`${TRANSLATION_KEY}.my_account_with_count`, { count: accountCount.value })
  : t(`${TRANSLATION_KEY}.my_account`));

function openOtherAccount(account: { index: number, expired?: boolean }) {
  if (account.expired) {
    reloginAccount(account.index);
    return;
  }
  switchAccount(account.index);
}
</script>

<template>
  <VDropdown
    theme="verbleif-popper-bounded"
    popper-class="account-menu-popper"
    placement="right"
    auto-size="min"
  >
    <template #popper>
      <div class="flex w-[340px] max-w-full flex-col bg-white dark:bg-dark-800">
        <div class="flex items-center gap-3 p-4" data-testid="navbar-account-card">
          <Avatar
            :name="authStore.user.value?.name"
            :avatar-url="authStore.user.value?.avatar?.contentUrl"
            size="lg"
            border-style="none"
            class="size-11! shrink-0 ring-0!"
          />
          <div class="flex min-w-0 flex-1 flex-col">
            <div class="flex items-center gap-2">
              <span class="truncate text-[15px]/5 font-medium text-[#212121] dark:text-gray-100">
                {{ authStore.user.value?.name }}
              </span>
              <VTag
                v-if="hasMultipleAccounts"
                variant="is-success"
                size="extra-small"
                icon-less
                :text="t(`${TRANSLATION_KEY}.active_tag`)"
                data-testid="navbar-active-tag"
              />
            </div>
            <div class="truncate text-xs/[17px] text-[#757575] dark:text-gray-400">
              {{ accountsStore.activeAccount.value?.username }}
            </div>
            <div v-if="activeContext" class="mt-1 flex items-center gap-1.5 text-xs/4 text-[#616161] dark:text-gray-300">
              <VIcon name="map-pin" class="size-3 shrink-0 text-[#757575] dark:text-gray-400" aria-hidden="true" />
              <span class="truncate" data-testid="navbar-active-context">{{ activeContext }}</span>
            </div>
          </div>
        </div>
        <div class="h-px bg-[#EEEEEE] dark:bg-dark-600" role="separator" />
        <!-- Every row section owns the same 6px above and below, so the dividers between them carry no margin. -->
        <div class="flex flex-col py-1.5" data-testid="navbar-menu-section">
          <div v-if="accountsStore.otherAccounts.value.length" class="flex flex-col" data-testid="navbar-other-accounts">
            <div class="px-4 pt-1 pb-1 text-[11px]/4 font-medium tracking-[0.04em] text-[#757575] uppercase dark:text-gray-400">
              {{ t(`${TRANSLATION_KEY}.other_accounts`) }}
            </div>
            <button
              v-for="account in visibleOtherAccounts"
              :key="account.index"
              v-close-popper.all
              type="button"
              class="flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-1.5 text-left text-sm/5 text-[#424242] hover:bg-[#FAFAFA] focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden focus-visible:ring-inset dark:text-gray-100 dark:hover:bg-dark-600"
              data-testid="navbar-account-item"
              @click="openOtherAccount(account)"
            >
              <Avatar :name="account.name" :avatar-url="account.avatarUrl" size="sm" border-style="none" class="shrink-0 ring-0!" />
              <span class="flex min-w-0 flex-1 flex-col">
                <span class="truncate text-sm/5 font-medium">{{ account.name }}</span>
                <span class="truncate text-xs/4 text-[#757575] dark:text-gray-400">{{ account.username }}</span>
              </span>
              <VTag
                v-if="account.expired"
                variant="is-warning"
                size="extra-small"
                icon-less
                :text="t(`${TRANSLATION_KEY}.relogin_tag`)"
                data-testid="navbar-account-expired"
              />
              <VIcon v-else name="repeat" class="size-3.5 shrink-0 text-[#BDBDBD]" aria-hidden="true" />
            </button>
            <button
              v-if="hasHiddenAccounts"
              v-close-popper.all
              type="button"
              class="block w-full text-left focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden focus-visible:ring-inset"
              data-testid="navbar-all-accounts"
              @click="openAccountsDialog()"
            >
              <NavbarSettingsItem icon="users" :text="t(`${TRANSLATION_KEY}.all_accounts`)" chevron>
                <template #trailing>
                  <span class="shrink-0 text-[13px] text-[#757575] dark:text-gray-400" data-testid="navbar-all-accounts-count">{{ accountCount }}</span>
                </template>
              </NavbarSettingsItem>
            </button>
          </div>
          <button
            v-close-popper.all
            type="button"
            class="block w-full text-left focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden focus-visible:ring-inset"
            data-testid="navbar-add-account"
            @click="addAccount()"
          >
            <NavbarSettingsItem variant="add" icon="plus" :text="t(`${TRANSLATION_KEY}.add_account`)" />
          </button>
        </div>
        <div class="h-px bg-[#EEEEEE] dark:bg-dark-600" role="separator" />
        <div class="flex flex-col py-1.5" data-testid="navbar-menu-section">
          <RouterLink v-close-popper.all to="/settings/my-account" class="focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden focus-visible:ring-inset">
            <NavbarSettingsItem icon="user" :text="t(`${TRANSLATION_KEY}.my_account`)" chevron />
          </RouterLink>
          <button
            v-if="isV2Enabled"
            v-close-popper.all
            type="button"
            class="block w-full text-left focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden focus-visible:ring-inset"
            data-testid="navbar-inbox"
            @click="openInbox"
          >
            <NavbarSettingsItem icon="inbox" :text="t('notifications.v2.inbox.title')" :count="notificationCount" />
          </button>
          <!-- The inbox replaces the notifications list in the new notification module. -->
          <NavbarNotifications v-if="!isV2Enabled" />
          <RouterLink v-close-popper.all :to="`/spaces?redirect=${route.path}`" class="focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden focus-visible:ring-inset">
            <NavbarSettingsItem icon="map-pin" :text="t(`${TRANSLATION_KEY}.location_switch`)" :sub-text="locationName ?? undefined" chevron />
          </RouterLink>
          <NavbarLocaleChange />
        </div>
        <div class="h-px bg-[#EEEEEE] dark:bg-dark-600" role="separator" />
        <div class="flex flex-col py-1.5" data-testid="navbar-menu-section">
          <button
            v-close-popper.all
            type="button"
            class="block w-full text-left focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden focus-visible:ring-inset"
            data-testid="navbar-logout"
            @click="requestLogout()"
          >
            <NavbarSettingsItem
              variant="danger"
              icon="log-out"
              :text="t(`${TRANSLATION_KEY}.logout`)"
              :sub-text="hasMultipleAccounts ? t(`${TRANSLATION_KEY}.logout_hint_accounts`, { count: accountCount }) : undefined"
            />
          </button>
        </div>
        <div class="flex w-full items-center justify-between border-t border-[#EEEEEE] bg-[#FAFAFA] px-4 pt-3.5 pb-4 text-xs text-[#757575] dark:border-dark-600 dark:bg-dark-900 dark:text-gray-400" data-testid="navbar-download-footer">
          <div>
            {{ t(`${TRANSLATION_KEY}.download_app`) }}
          </div>
          <div class="flex gap-3 text-sm">
            <a
              class="rounded-xs hover:text-primary-500 focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden active:text-primary-700"
              href="https://verbleif.com/android"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Android"
            >
              <VIcon name="brand-android" class="size-3.5" aria-hidden="true" />
            </a>
            <a
              class="rounded-xs hover:text-primary-500 focus-visible:ring-2 focus-visible:ring-[#4CAF58] focus-visible:outline-hidden active:text-primary-700"
              href="https://verbleif.com/ios"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="iOS"
            >
              <VIcon name="brand-apple" class="size-3.5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </template>
    <button
      type="button"
      class="relative block rounded-full focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden"
      :aria-label="triggerLabel"
    >
      <Avatar
        size="md"
        main
        :name="authStore.user.value?.name"
        :avatar-url="authStore.user.value?.avatar?.contentUrl"
        :notifications="notificationCount"
        class="cursor-pointer"
      />
      <span
        v-if="hasMultipleAccounts"
        class="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-white text-[10px] leading-none font-semibold text-gray-800 shadow-sm ring-1 ring-gray-200 dark:bg-dark-700 dark:text-gray-100 dark:ring-dark-500"
        aria-hidden="true"
        data-testid="navbar-account-count"
      >
        {{ accountCount }}
      </span>
    </button>
  </VDropdown>
</template>

<style>
/* The popover owns the chrome of the account menu board: 340px, radius 16, light border, two-layer shadow. */
.account-menu-popper .v-popper__inner {
  padding: 0;
  overflow: hidden;
  border: 1px solid #eee;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 16px 40px rgb(0 0 0 / 0.14), 0 2px 6px rgb(0 0 0 / 0.06);
}

body[data-theme="dark"] .account-menu-popper .v-popper__inner {
  border-color: var(--color-dark-600, #3a3a3a);
  background: var(--color-dark-800, #1f1f1f);
}
</style>
